import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import QRCode from 'qrcode'
import { z } from 'zod'
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, USERNAME_PATTERN } from '@xingroute/schema'
import { api } from '../api/client.js'
import { useAuth } from '../auth/AuthContext.js'
import { Button } from '../components/ui/Button.js'
import { Card } from '../components/ui/Card.js'
import { Field, Input } from '../components/ui/Field.js'
import { friendlyError } from '../lib/friendlyError.js'

interface Props {
  mode: 'signin' | 'signup' | 'admin'
}

type AuthT = (key: string) => string

export function makeAuthSchema(t: AuthT) {
  return z.object({
    username: z
      .string({
        required_error: t('authValidation.usernameRequired'),
        invalid_type_error: t('authValidation.usernameRequired'),
      })
      .min(3, t('authValidation.usernameTooShort'))
      .max(32, t('authValidation.usernameTooLong'))
      .regex(USERNAME_PATTERN, t('authValidation.usernamePattern')),
    password: z
      .string({
        required_error: t('authValidation.passwordRequired'),
        invalid_type_error: t('authValidation.passwordRequired'),
      })
      .min(PASSWORD_MIN_LENGTH, t('authValidation.passwordTooShort'))
      .max(PASSWORD_MAX_LENGTH, t('authValidation.passwordTooLong')),
    displayName: z
      .string({
        required_error: t('authValidation.displayNameInvalid'),
        invalid_type_error: t('authValidation.displayNameInvalid'),
      })
      .max(64, t('authValidation.displayNameTooLong')),
  })
}

export function AuthPage({ mode }: Props) {
  const auth = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation('common')
  const authSchema = makeAuthSchema(t)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [qr, setQr] = useState<{
    dataUrl: string
    expiresAt: string
    status: 'loading' | 'pending' | 'error'
  }>({ dataUrl: '', expiresAt: '', status: 'loading' })
  const [qrRetry, setQrRetry] = useState(0)

  const isAdmin = mode === 'admin'

  if (auth.status === 'authed') {
    return <Navigate to={isAdmin ? '/admin' : '/discover'} replace />
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const parsed = authSchema.safeParse({ username, password, displayName: username })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t('authValidation.invalid'))
      return
    }

    setSubmitting(true)
    try {
      await auth.login(parsed.data.username, parsed.data.password)
      const params = new URLSearchParams(location.search)
      const next = params.get('next') ?? (isAdmin ? '/admin' : '/discover')
      navigate(next, { replace: true })
    } catch (err) {
      setError(friendlyError(err, t))
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    if (isAdmin || auth.status === 'authed') return
    let cancelled = false
    let timer: number | undefined

    async function startQrLogin() {
      setQr((current) => ({ ...current, status: 'loading' }))
      setError(null)
      try {
        const challenge = await api.createQrLoginChallenge()
        const url = new URL(challenge.qr_payload)
        const challengeId = url.searchParams.get('challenge_id') ?? challenge.challenge_id
        const secret = url.searchParams.get('secret') ?? ''
        const dataUrl = await QRCode.toDataURL(challenge.qr_payload, {
          margin: 1,
          width: 224,
        })
        if (cancelled) return
        setQr({ dataUrl, expiresAt: challenge.expires_at, status: 'pending' })

        async function poll(delayMs: number) {
          timer = window.setTimeout(async () => {
            try {
              const result = await api.pollQrLoginChallenge(challengeId, secret)
              if (cancelled) return
              if (result.status === 'confirmed') {
                auth.applyAuthResponse(result.auth)
                const params = new URLSearchParams(location.search)
                navigate(params.get('next') ?? '/discover', { replace: true })
                return
              }
              await poll(result.poll_after_ms)
            } catch (err) {
              if (cancelled) return
              setQr((current) => ({ ...current, status: 'error' }))
              setError(friendlyError(err, t))
            }
          }, delayMs)
        }
        await poll(challenge.poll_after_ms)
      } catch (err) {
        if (cancelled) return
        setQr((current) => ({ ...current, status: 'error' }))
        setError(friendlyError(err, t))
      }
    }

    void startQrLogin()
    return () => {
      cancelled = true
      if (timer !== undefined) window.clearTimeout(timer)
    }
  }, [auth, isAdmin, location.search, navigate, qrRetry, t])

  if (isAdmin) {
    return (
      <div className="mx-auto max-w-sm">
        <h1 className="text-lg font-semibold tracking-tight text-[var(--xr-text-primary)] mb-5">
          {t('auth.adminTitle')}
        </h1>
        <Card>
          <form onSubmit={onSubmit} noValidate className="space-y-4" data-testid="auth-form">
            <Field label={t('auth.usernameLabel')}>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                minLength={3}
                maxLength={32}
                name="username"
                autoComplete="username"
                data-testid="auth-username"
              />
            </Field>
            <Field label={t('auth.passwordLabel')}>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                name="password"
                autoComplete="current-password"
                data-testid="auth-password"
              />
            </Field>
            {error && <p className="text-sm text-[var(--xr-danger)]">{error}</p>}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full"
              data-testid="auth-submit"
            >
              {submitting ? '...' : t('auth.signInButton')}
            </Button>
          </form>
        </Card>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-md">
      <h1 className="text-lg font-semibold tracking-tight text-[var(--xr-text-primary)] mb-1">
        {t('auth.signInTitle')}
      </h1>
      <p className="text-xs text-[var(--xr-text-secondary)] mb-5">{t('auth.signInDescription')}</p>
      <Card>
        <div className="rounded-2xl border border-[var(--xr-border)] bg-[var(--xr-surface-muted)] p-4 text-center">
          <p className="text-sm font-medium text-[var(--xr-text-primary)]">{t('auth.qrTitle')}</p>
          <p className="mt-1 text-xs text-[var(--xr-text-secondary)]">{t('auth.qrDescription')}</p>
          <div className="mt-4 flex min-h-56 items-center justify-center rounded-xl bg-white p-3">
            {qr.status === 'pending' && qr.dataUrl ? (
              <img src={qr.dataUrl} width={224} height={224} alt={t('auth.qrAlt')} />
            ) : (
              <span className="text-sm text-[var(--xr-text-secondary)]">
                {qr.status === 'loading' ? t('auth.qrLoading') : t('auth.qrError')}
              </span>
            )}
          </div>
          {qr.expiresAt && (
            <p className="mt-3 text-[11px] text-[var(--xr-text-secondary)]">
              {t('auth.qrExpires', { time: new Date(qr.expiresAt).toLocaleTimeString() })}
            </p>
          )}
          {qr.status === 'error' && (
            <Button
              type="button"
              variant="secondary"
              className="mt-3"
              onClick={() => setQrRetry((value) => value + 1)}
            >
              {t('auth.qrRetry')}
            </Button>
          )}
        </div>
        {error && <p className="mt-4 text-sm text-[var(--xr-danger)]">{error}</p>}
      </Card>
    </div>
  )
}
